import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Obsolete-stock / return-candidate detection.
 *
 * A purpose-built report over the inventory-policy grain (SKU × warehouse) that
 * surfaces items worth returning to a supplier: stock that is physically on hand
 * now, has been *sitting* (never drew down over a look-back window), is *not
 * selling*, and — optionally — was purchased recently enough to still fall inside
 * a vendor return window.
 *
 * Unlike the generic query builder this is a fixed query shape, so it runs its
 * own dedicated parameterized SQL (latest-snapshot LATERAL for current on-hand,
 * a min/avg-over-window for the sitting signal, correlated sales + purchase
 * look-backs) rather than going through the query-builder IR/compiler.
 *
 * See docs/projectplans/obsolete-return-candidates.md.
 */

// ─── Sort ─────────────────────────────────────────────────────────────────────

export const ReturnCandidateSortSchema = z.enum([
  "onHandDesc",
  "minOnHandDesc",
  "salesAsc",
  "skuAsc",
]);
export type ReturnCandidateSort = z.infer<typeof ReturnCandidateSortSchema>;

// ─── Run ────────────────────────────────────────────────────────────────────

export const ReturnCandidatesInputSchema = z.object({
  // ── SKU classification (inventory policy) ──────────────────────────────────
  /** Keep only these GAINS stock-status codes (e.g. stocked / non-stock). */
  stockStatus: z.array(z.string()).optional(),
  /** Keep only these GAINS popularity codes. */
  popularityCode: z.array(z.string()).optional(),

  // ── Sales velocity (the "not selling" gate) ────────────────────────────────
  /**
   * Maximum units sold (sum of sales-line quantity) over the look-back window
   * for the SKU at its warehouse. Default 0 → "no sales at all".
   */
  maxSalesUnits: z.number().nonnegative().optional().default(0),
  /** Trailing window, in months, over which sales are measured. */
  salesLookbackMonths: z.number().int().min(1).max(60).optional().default(12),
  /**
   * Optional sell-through gate: keep only SKUs whose units sold over the window
   * are at most this percent of current on-hand (e.g. 10 → "sold ≤10% of the
   * pile"). AND-ed with `maxSalesUnits`. Omit to ignore. (Percent of on-hand
   * quantity — no item cost is available on this grain to do percent of value.)
   */
  maxSalesPercentOfStock: z.number().nonnegative().max(100).optional(),

  // ── Sitting / aging (the "has been sitting" gate) ──────────────────────────
  /**
   * Trailing window, in months, over which the SKU must have *never* drawn down
   * to zero on hand (min on-hand > 0) — i.e. a pile that has just been sitting.
   */
  sittingMonths: z.number().int().min(1).max(60).optional().default(12),
  /** Minimum *current* on-hand quantity to qualify (excludes already-depleted). */
  minOnHand: z.number().nonnegative().optional().default(1),

  // ── Returnability (vendor return window) ───────────────────────────────────
  /**
   * When set, keep only SKUs with a purchase-order line received within this
   * many months (the vendor stock-rotation window). Omit to ignore.
   */
  requirePurchaseWithinMonths: z.number().int().min(1).max(60).optional(),

  // ── Scope (each accepts multiple; empty/omitted = all) ──────────────────────
  divisionIds: z.array(z.string().uuid()).optional(),
  warehouseIds: z.array(z.string().uuid()).optional(),
  /**
   * Limit to SKUs purchased from these suppliers; when set, the buy-quantity
   * columns + last-purchase fields are also scoped to those suppliers.
   */
  supplierIds: z.array(z.string().uuid()).optional(),

  // ── Sort + paging ──────────────────────────────────────────────────────────
  sortBy: ReturnCandidateSortSchema.optional().default("onHandDesc"),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(500).optional().default(50),
});

export type ReturnCandidatesInput = z.infer<typeof ReturnCandidatesInputSchema>;

export const ReturnCandidateRowSchema = z.object({
  /** `im_products_v3.key` (the SKU): the only identity this report uses. */
  productV3Key: z.string(),
  productSku: z.string().nullable(),
  /** `display_description`, NULL for most of the catalog. */
  productName: z.string().nullable(),
  warehouseId: z.string().uuid(),
  warehouseName: z.string(),
  divisionName: z.string().nullable(),
  stockStatus: z.string().nullable(),
  popularityCode: z.string().nullable(),
  /** On-hand from the most recent snapshot for this SKU × warehouse. */
  currentOnHand: z.number(),
  currentAvailable: z.number(),
  /** Average / minimum on-hand across the sitting window. */
  avgOnHand: z.number(),
  minOnHand: z.number(),
  /** Sales over the look-back window. */
  salesUnits: z.number(),
  salesOrderLines: z.number(),
  lastSaleDate: z.string().nullable(),
  /** Most recent purchase of this SKU into this warehouse + its supplier. */
  lastPurchaseDate: z.string().nullable(),
  lastPurchaseSupplier: z.string().nullable(),
  /** All distinct suppliers this SKU was purchased from (within the PO scan window). */
  suppliers: z.array(z.string()),
  /** True when the last purchase falls inside the requested return window. */
  purchasedWithinWindow: z.boolean(),
  /** Units ordered from suppliers into this warehouse over trailing windows. */
  boughtUnits6mo: z.number(),
  boughtUnits12mo: z.number(),
  boughtUnits24mo: z.number(),
  /** GAINS order-up-to ceiling, if a policy exists. */
  maxQuantity: z.number().nullable(),
  /**
   * Suggested quantity to return: on-hand above the policy ceiling, or all of
   * it when there is no ceiling. Never negative.
   */
  suggestedReturnQty: z.number(),
});

export type ReturnCandidateRow = z.infer<typeof ReturnCandidateRowSchema>;

export const ReturnCandidatesResponseSchema = z.object({
  rows: z.array(ReturnCandidateRowSchema),
  totalRows: z.number().int().nonnegative(),
  /** Snapshot date the current-on-hand figures are drawn from (latest in org). */
  effectiveSnapshotDate: z.string().nullable(),
});

export type ReturnCandidatesResponse = z.infer<
  typeof ReturnCandidatesResponseSchema
>;

export const ReturnCandidatesExportResponseSchema = z.object({
  rows: z.array(ReturnCandidateRowSchema),
  totalRows: z.number().int().nonnegative(),
  /** True when the result exceeded the export cap and was truncated. */
  truncated: z.boolean(),
});

export type ReturnCandidatesExportResponse = z.infer<
  typeof ReturnCandidatesExportResponseSchema
>;

// ─── Facets (filter options) ──────────────────────────────────────────────────

export const ReturnCandidateFacetsResponseSchema = z.object({
  stockStatuses: z.array(z.string()),
  popularityCodes: z.array(z.string()),
  divisions: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  warehouses: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      displayCode: z.string().nullable().default(null),
    }),
  ),
  suppliers: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
});

export type ReturnCandidateFacetsResponse = z.infer<
  typeof ReturnCandidateFacetsResponseSchema
>;

// ─── Contract ───────────────────────────────────────────────────────────────

export const returnCandidatesContracts = {
  run: oc
    .input(ReturnCandidatesInputSchema)
    .output(ReturnCandidatesResponseSchema)
    .route({
      method: "GET",
      summary: "List obsolete stock return candidates",
      description:
        "Paged SKU × warehouse stock that is on hand, sitting and not selling, optionally still inside a vendor return window. Filter by sales, months, division, warehouse, supplier.",
    }),
  facets: oc
    .input(z.object({}))
    .output(ReturnCandidateFacetsResponseSchema)
    .route({
      method: "GET",
      summary: "Get return candidate report filter options",
      description:
        "Returns the stock statuses, popularity codes, divisions and warehouses available to filter the obsolete-stock return-candidates report.",
    }),
  export: oc
    .input(ReturnCandidatesInputSchema)
    .output(ReturnCandidatesExportResponseSchema)
    .route({
      method: "GET",
      summary: "Export obsolete stock return candidates",
      description:
        "Returns all return-candidate rows for the same filters as the report, unpaginated but capped, for CSV download.",
    }),
} as const;
